/* FRX-43440: Accelerated checkout inside Tea Forte's sticky Add to Bag.
 * VIP customer check: rely on the store-rendered status (vip-program).
 * A second, independent Shopify product form is required because the sticky
 * markup is outside the main product form. Never clone the Shopify wallet DOM.
 * The form is synchronized with the PDP before checkout is initiated.
 */
(() => {
  'use strict';

  function initStickyWallet() {
    const sticky = document.querySelector('[data-sticky-atc]');
    const stickyForm = sticky?.querySelector('form.pdp-sticky-atc__wallet-form');
    if (!stickyForm || stickyForm.dataset.frxStickyInitialized === 'true') return;
    stickyForm.dataset.frxStickyInitialized = 'true';

    const variantInput = stickyForm.querySelector('[data-sticky-wallet-variant]');
    const quantityInput = stickyForm.querySelector('[data-sticky-wallet-quantity]');
    const quantityLabel = sticky.querySelector('[data-sticky-atc-qty]');
    const stickyAddButton = sticky.querySelector('[data-sticky-atc-button]');
    const variantData = document.querySelector('[data-sticky-atc-product-json]');
    let variants = [];

    if (variantData) {
      try {
        variants = JSON.parse(variantData.textContent)?.variants || [];
      } catch (error) {
        console.warn('[FRX-43440] Cannot read sticky product variants.', error);
      }
    }

    const getMainContainer = () => document.querySelector('[id^="ProductInfo-"]');
    const getMainForm = () => getMainContainer()?.querySelector('hh-product-form form[action*="/cart/add"]')
      || getMainContainer()?.querySelector('form[action*="/cart/add"]');

    function removeMirrorInputs() {
      stickyForm.querySelectorAll('[data-sticky-wallet-mirror]').forEach((input) => input.remove());
    }

    function addMirrorInput(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(value);
      input.setAttribute('data-sticky-wallet-mirror', '');
      stickyForm.appendChild(input);
    }

    function syncStickyWallet() {
      if (!stickyForm.isConnected) return;
      const mainForm = getMainForm();
      const mainContainer = getMainContainer();
      const offer = mainContainer?.querySelector('.vip-offer--pdp[data-vip-offer-container]');
      const memberRadio = offer?.querySelector('[data-vip-price-option="member"]');
      const subscriberCheckbox = offer?.querySelector('.vip-offer__checkbox');

      // Prevent checkout with stale data if the main PDP form is missing.
      if (!mainForm || !variantInput || !quantityInput) {
        stickyForm.dataset.stickyWalletUnavailable = 'true';
        return;
      }

      const mainVariantId = String(mainForm.querySelector('input[name="id"]')?.value || '').trim();
      const qtyText = quantityLabel?.textContent?.trim();
      const qtyFromMain = mainForm.querySelector('input[name="quantity"]')?.value;
      const quantity = Math.max(1, parseInt(qtyText || qtyFromMain || '1', 10) || 1);

      variantInput.value = mainVariantId;
      quantityInput.value = String(quantity);
      removeMirrorInputs();

      const selectedVariant = variants.find((variant) => String(variant.id) === mainVariantId);
      const invalidVariant = !/^\d+$/.test(mainVariantId) || selectedVariant?.available === false;
      const disabledStickyCTA = Boolean(
        stickyAddButton?.disabled ||
        stickyAddButton?.classList.contains('is-disabled') ||
        stickyAddButton?.classList.contains('is-notify')
      );

      let invalidVIP = false;
      let isExistingVipCustomer = false;
      let membershipAddedToCheckout = false;
      if (offer) {
        if (!memberRadio || !subscriberCheckbox) {
          invalidVIP = true;
        } else {
          // IMPORTANT: An existing VIP customer must NEVER receive another
          // membership subscription. Both flags are server-rendered by
          // vip-program (customer tags); the CSS class is a fallback.
          const vipSelector = offer.querySelector('[data-pdp-pricing-selector]');
          isExistingVipCustomer =
            vipSelector?.dataset.vipCustomer === 'true' ||
            offer.dataset.vipOverride === 'true' ||
            offer.classList.contains('vip-offer--existing-vip');

          // 'vip-offer--vip-in-cart' is NOT proof of an active VIP customer:
          // accelerated checkout from the PDP skips existing cart contents.
          // Only omit membership when the account is already an active VIP.
          const requiresMembership = memberRadio.checked && !isExistingVipCustomer;

          if (requiresMembership) {
            const subscriberVariant = String(subscriberCheckbox.dataset.subscriberVariantId || '').trim();
            const subscriberSellingPlan = String(subscriberCheckbox.dataset.subscriberSellingPlanId || '').trim();
            if (!/^\d+$/.test(subscriberVariant) || !/^\d+$/.test(subscriberSellingPlan)) {
              invalidVIP = true;
            } else if (mainVariantId !== subscriberVariant) {
              addMirrorInput('shopify-additional-items[1][id]', subscriberVariant);
              addMirrorInput('shopify-additional-items[1][quantity]', '1');
              addMirrorInput('shopify-additional-items[1][selling_plan]', subscriberSellingPlan);
              membershipAddedToCheckout = true;
            }
            // If the main product itself is the membership variant,
            // never append a duplicate membership item.
          }
        }
      }

      // Keep the main product's subscription setting when relevant.
      const sellingPlan = mainForm.querySelector('input[name="selling_plan"]:checked')
        || mainForm.querySelector('select[name="selling_plan"]')
        || mainForm.querySelector('input[name="selling_plan"]');
      if (sellingPlan?.value) {
        addMirrorInput('selling_plan', sellingPlan.value);
      }

      // Preserve ordinary text properties used by customizable products.
      // A file upload cannot be mirrored into an independent form safely.
      let unsupportedProperty = false;
      mainForm.querySelectorAll('[name^="properties["]').forEach((field) => {
        if (field.disabled || !field.name) return;
        if (field.type === 'checkbox' || field.type === 'radio') {
          if (!field.checked) return;
        }
        if (field.type === 'file') {
          if (field.files?.length) unsupportedProperty = true;
          return;
        }
        if (field.value) addMirrorInput(field.name, field.value);
      });

      const unavailable = invalidVariant || invalidVIP || disabledStickyCTA || unsupportedProperty;
      stickyForm.dataset.stickyWalletExistingVip = isExistingVipCustomer ? 'true' : 'false';
      stickyForm.dataset.stickyWalletMembershipAdded = membershipAddedToCheckout ? 'true' : 'false';
      stickyForm.dataset.stickyWalletUnavailable = unavailable ? 'true' : 'false';
      stickyForm.dataset.stickyWalletReady = 'true';
    }

    let scheduled = false;
    function scheduleSync() {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        syncStickyWallet();
      });
    }

    // Capture the latest values before Shopify handles an accelerated click.
    stickyForm.addEventListener('pointerdown', syncStickyWallet, true);
    stickyForm.addEventListener('click', syncStickyWallet, true);

    document.addEventListener('change', scheduleSync);
    document.addEventListener('input', (event) => {
      if (event.target?.matches?.('[name="quantity"], [name="id"], [name="selling_plan"]')) scheduleSync();
    });
    ['vipPricing:changed', 'vipOffer:stateUpdated', 'variant:change', 'variantChange', 'cart:updated', 'cart:changed'].forEach((eventName) => {
      document.addEventListener(eventName, scheduleSync);
    });
    window.addEventListener('pageshow', scheduleSync);

    if (quantityLabel) {
      new MutationObserver(scheduleSync).observe(quantityLabel, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
    if (stickyAddButton) {
      new MutationObserver(scheduleSync).observe(stickyAddButton, {
        attributes: true,
        attributeFilter: ['disabled', 'class', 'aria-disabled']
      });
    }

    syncStickyWallet();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyWallet, { once: true });
  } else {
    initStickyWallet();
  }
  document.addEventListener('shopify:section:load', initStickyWallet);
})();